import { RcFile } from "antd/es/upload";
import { ThemeConfigForm } from "../interface/configuration"
import { ICustomization } from "../redux/reducer/configuration"
import { WelcomeParams } from "../components/organisms/Welcome";
import { ApplicantInfoParams } from "../components/organisms/ApplicantInfo";
import { CompleteParams } from "../components/organisms/Complete";
import { TranscriptParams } from "../components/organisms/Transcript";

const loremIpsum = {
    header: 'Lorem Ipsum',
    text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.",
}

const getBase64 = (file: RcFile): Promise<string> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
});

const mapConfig = {
    toForm: (config: ICustomization): ThemeConfigForm => ({
        logo: config.logo ? [{ name: 'logo', uid: '' }] : undefined,
        favicon: config.favicon ? [{ name: 'favicon', uid: '' }] : undefined,
        fontLink: config.fontLink,
        noHeader: config.noHeader,
        'configuration.components.Layout.headerBg': config.configuration?.components?.Layout?.headerBg,
        'configuration.token.colorBgLayout': config.configuration?.token?.colorBgLayout,
        'configuration.token.colorPrimary': config.configuration?.token?.colorPrimary,
        'configuration.token.colorError': config.configuration?.token?.colorError,
        'configuration.token.colorSuccess': config.configuration?.token?.colorSuccess,
        'configuration.token.colorInfo': config.configuration?.token?.colorInfo,
        'configuration.token.colorWarning': config.configuration?.token?.colorWarning,
        'configuration.token.colorText': config.configuration?.token?.colorText,
        'configuration.token.colorTextHeading': config.configuration?.token?.colorTextHeading,
        'configuration.token.colorTextLightSolid': config.configuration?.token?.colorTextLightSolid,
        "configuration.components.Button.fontWeight": config.configuration?.components?.Button?.fontWeight,
        "configuration.components.Button.paddingInline": config.configuration?.components?.Button?.paddingInline,
        "configuration.components.Button.controlHeight": config.configuration?.components?.Button?.controlHeight,
        "configuration.components.Button.borderRadius": config.configuration?.components?.Button?.borderRadius,
        'configuration.token.fontFamily': config.configuration?.token?.fontFamily,
        'configuration.token.fontSize': config.configuration?.token?.fontSize?.toString(),
        htmlTitle: config.htmlTitle,
    }),
    fromForm: async (form: ThemeConfigForm): Promise<ICustomization> => ({
        logo: form.logo?.length ? form.logo[0]?.originFileObj ? await getBase64(form.logo[0]?.originFileObj as RcFile) : form.logo[0]?.name : undefined,
        favicon: form.favicon?.length ? form.favicon[0]?.originFileObj ? await getBase64(form.favicon[0]?.originFileObj as RcFile) : form.favicon[0]?.name : undefined,
        fontLink: form.fontLink,
        htmlTitle: form.htmlTitle,
        noHeader: form.noHeader,
        configuration: {
            components: {
                Layout: {
                    headerBg: typeof form['configuration.components.Layout.headerBg'] === 'string' ? form['configuration.components.Layout.headerBg'] : form['configuration.components.Layout.headerBg']?.toHexString(),
                },
                Button: {
                    fontWeight: typeof form['configuration.components.Button.fontWeight'] === 'string' ? Number(form['configuration.components.Button.fontWeight']) : undefined,
                    paddingInline: typeof form['configuration.components.Button.paddingInline'] === 'string' ? Number(form['configuration.components.Button.paddingInline']) : undefined,
                    controlHeight: typeof form['configuration.components.Button.controlHeight'] === 'string' ? Number(form['configuration.components.Button.controlHeight']) : undefined,
                    borderRadius: typeof form['configuration.components.Button.borderRadius'] === 'string' ? Number(form['configuration.components.Button.borderRadius']) : undefined,
                }
            },
            token: {
                colorBgLayout: typeof form['configuration.token.colorBgLayout'] === 'string' ? form['configuration.token.colorBgLayout'] : form['configuration.token.colorBgLayout']?.toHexString(),
                colorPrimary: typeof form['configuration.token.colorPrimary'] === 'string' ? form['configuration.token.colorPrimary'] : form['configuration.token.colorPrimary']?.toHexString(),
                colorError: typeof form['configuration.token.colorError'] === 'string' ? form['configuration.token.colorError'] : form['configuration.token.colorError']?.toHexString(),
                colorSuccess: typeof form['configuration.token.colorSuccess'] === 'string' ? form['configuration.token.colorSuccess'] : form['configuration.token.colorSuccess']?.toHexString(),
                colorInfo: typeof form['configuration.token.colorInfo'] === 'string' ? form['configuration.token.colorInfo'] : form['configuration.token.colorInfo']?.toHexString(),
                colorWarning: typeof form['configuration.token.colorWarning'] === 'string' ? form['configuration.token.colorWarning'] : form['configuration.token.colorWarning']?.toHexString(),
                colorText: typeof form['configuration.token.colorText'] === 'string' ? form['configuration.token.colorText'] : form['configuration.token.colorText']?.toHexString(),
                colorTextHeading: typeof form['configuration.token.colorTextHeading'] === 'string' ? form['configuration.token.colorTextHeading'] : form['configuration.token.colorTextHeading']?.toHexString(),
                fontFamily: form['configuration.token.fontFamily'],
                fontSize: form['configuration.token.fontSize'] ? Number(form['configuration.token.fontSize']?.replace(/[^0-9]+/g, "")) : undefined,
                colorTextLightSolid: typeof form['configuration.token.colorTextLightSolid'] === 'string' ? form['configuration.token.colorTextLightSolid'] : form['configuration.token.colorTextLightSolid']?.toHexString(),
            }
        }
    }),
}

export interface SignatureField {
    fieldId: string;
    fieldName?: string;
    defaultValue?: string;
    populatedValue?: string;
}

export type SignatureStatus = 'Awaiting' | 'Signed' | 'Declined';

export interface SignatureDocument {
    id: string;
    documentId: string;
    name: string;
    required: boolean;
    fields: SignatureField[];
}

export const newDocument = (name: string): SignatureDocument => ({
    id: 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    }),
    documentId: "",
    name,
    required: true,
    fields: [],
})

export enum ConfigStepId {
    welcome = 'welcome',
    info = 'info',
    transcript = 'transcript',
    business = 'business',
    complete = 'complete',
}

export interface ConfigStep {
    id: ConfigStepId;
    name: string;
    description: string;
    enabled: boolean;
    required: boolean;
    params: any;
    nameOverride?: string;
    conditionalEnabling?: boolean;
}

export interface ConfigStepWelcome extends ConfigStep {
    id: ConfigStepId.welcome;
    params: WelcomeParams;
}

export interface ConfigStepInfo extends ConfigStep {
    id: ConfigStepId.info;
    params: ApplicantInfoParams;
}

export interface ConfigStepTranscript extends ConfigStep {
    id: ConfigStepId.transcript;
    params: TranscriptParams;
}

export interface BusinessAddress {
    lineOne?: string;
    lineTwo?: string;
    city?: string;
    state?: string;
    postalCode?: string;
}

export interface BusinessParams {
    allowAddition: boolean;
    document: SignatureDocument;
    signatureMode: 'individual' | 'shared';
    liveness?: {
        enabled: boolean;
        beforeSignature?: boolean;
        actionNeeded?: boolean;
        displayText?: string;
    };
    forms: { formName: string; taxYears: number; }[];
    useTenant?: boolean;
    overrideTenantId?: string;
}

export interface ConfigStepBusiness extends ConfigStep {
    id: ConfigStepId.business;
    params: BusinessParams;
}

export interface ConfigStepComplete extends ConfigStep {
    id: ConfigStepId.complete;
    params: CompleteParams;
}

export type ConfigStepType = ConfigStepWelcome | ConfigStepInfo | ConfigStepTranscript | ConfigStepBusiness | ConfigStepComplete;

const possibleSteps: ConfigStepType[] = [
    {
        id: ConfigStepId.welcome,
        name: 'Welcome',
        description: 'Initial client greeting',
        enabled: false,
        required: false,
        params: {
            jsonContent: {}
        },
    },
    {
        id: ConfigStepId.info,
        name: 'Information',
        description: 'Web Form for clients to enter their personal information',
        enabled: false,
        required: false,
        params: {},
    },
    {
        id: ConfigStepId.transcript,
        name: 'Transcript',
        description: 'Transcript Authorization',
        enabled: true,
        required: true,
        params: {
            useTenant: false,
            authorization: {
                order: ['consent', 'tia', 'liveness', 'approval'],
                consent: { enabled: true },
                tia: { enabled: true },
                approval: { enabled: false },
            },
            forms: [],
        },
    },
    {
        id: ConfigStepId.business,
        name: 'Business',
        description: 'Business Authorization for Tax Transcripts',
        enabled: false,
        required: false,
        params: {
            allowAddition: true,
            document: newDocument('Business Authorization'),
            signatureMode: 'individual',
            liveness: { enabled: false },
            forms: [],
        },
    },
    {
        id: ConfigStepId.complete,
        name: 'Complete',
        description: 'Thank you and goodbye to client',
        enabled: false,
        required: false,
        params: {},
    },
];

const getSteps = (institutionSteps: ConfigStepType[]): ConfigStep[] => {
    const instOrder: { [id: string]: number; } = institutionSteps.reduce((acc, { id }, idx) => ({ ...acc, [id]: idx }), {} as { [id: string]: number; });
    const allSteps = structuredClone(possibleSteps);
    return allSteps.map((step: ConfigStep) => {
        const idx = institutionSteps.findIndex(({ id }) => step.id === id);
        if (idx > -1) {
            return {
                ...step,
                enabled: institutionSteps[idx].enabled,
                conditionalEnabling: institutionSteps[idx].conditionalEnabling ?? false,
            }
        }
        return step;
    }).sort((a: ConfigStep, b: ConfigStep) => {
        return (instOrder[a.id] ?? 0) - (instOrder[b.id] ?? 0)
    });
}

export {
    loremIpsum,
    mapConfig,
    possibleSteps,
    getSteps,
}
